import { Fragment, useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';

import { editarBreadcrumb } from '../../../actions/breadcrumb';
import { obtenerOrden, limpiarDetalleOrden } from '../../../actions/ordenesDevolucion';
import { obtenerPaquete } from '../../../actions/configuracion';

import CompletarOrdenDevolucionModal from './modals/CompletarOrdenDevolucionModal';
import EliminarOrdenDevolucionModal from './modals/EliminarOrdenDevolucionModal';

const formatDate = (value) => {
    if (!value) return 'Sin definir';
    const d = new Date(value);
    if (isNaN(d.getTime())) return value;
    return d.toLocaleDateString('es-MX');
};

const renderBadgeStatus = (status) => {
    const s = Number(status);
    if (s === 1) return <span className="badge bg-info text-dark">Creada</span>;
    if (s === 2) return <span className="badge bg-success">Completada</span>;
    if (s === 3) return <span className="badge bg-warning text-dark">Cancelada</span>;
    if (s === 4) return <span className="badge bg-secondary">Eliminada</span>;
    return <span className="badge bg-secondary">Sin definir</span>;
};

const formatNumber = (value) => {
    const v = Number(value || 0);
    return new Intl.NumberFormat('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 }).format(v);
};

const buildUbicacionLabel = (mov) => {
    if (!mov) return '—';
    const parts = [];
    if (mov?.area?.nombre) parts.push(mov.area.nombre);
    if (mov?.organizador?.nombre) parts.push(mov.organizador.nombre);
    if (mov?.ubicacion?.nombre) parts.push(mov.ubicacion.nombre);
    return parts.join(' / ') || 'Sin ubicación';
};

const DetalleOrdenDevolucion = () => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const { id } = useParams();

    const { detalle: orden, loadingDetalle } = useSelector(state => state.ordenesDevolucion);

    const [modalCompletar, setModalCompletar] = useState(false);
    const [modalEliminar, setModalEliminar] = useState(false);

    const [paquetesExpandidos, setPaquetesExpandidos] = useState({});
    const [componentesPorPaquete, setComponentesPorPaquete] = useState({});
    const [cargandoComponentes, setCargandoComponentes] = useState({});

    const togglePaqueteExpandido = async (rowId, idPaqueteCatalogo) => {
        const abierto = !!paquetesExpandidos[rowId];
        setPaquetesExpandidos((prev) => ({ ...prev, [rowId]: !abierto }));

        if (abierto || componentesPorPaquete[rowId] || !idPaqueteCatalogo) return;

        setCargandoComponentes((prev) => ({ ...prev, [rowId]: true }));
        const detalle = await dispatch(obtenerPaquete(idPaqueteCatalogo));
        const comps = Array.isArray(detalle?.productos) ? detalle.productos : [];
        setComponentesPorPaquete((prev) => ({ ...prev, [rowId]: comps }));
        setCargandoComponentes((prev) => ({ ...prev, [rowId]: false }));
    };

    useEffect(() => {
        if (!id) return;

        dispatch(obtenerOrden(id));

        return () => {
            dispatch(limpiarDetalleOrden());
        };
    }, [dispatch, id]);

    useEffect(() => {
        dispatch(editarBreadcrumb([
            { activo: false, nombre: 'Proyectos', path: '/proyectos' },
            { activo: false, nombre: orden?.proyecto?.nombre || 'Proyecto', path: `/proyectos/detalle/${orden?.proyecto?._id || ''}` },
            { activo: true, nombre: `Devolución ${orden?.folio || ''}`.trim(), path: `/proyectos/devoluciones/detalle/${id}` }
        ]));
    }, [dispatch, id, orden]);

    const recargar = () => {
        dispatch(obtenerOrden(id));
    };

    if (loadingDetalle || !orden) {
        return (
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <h3 className="my-5">Cargando orden de devolución...</h3>
                </div>
            </div>
        );
    }

    const todosLosProductos = Array.isArray(orden.productos) ? orden.productos : [];
    const productos = todosLosProductos.filter(p => !!p.producto);
    const paquetes = todosLosProductos.filter(p => !p.producto && !!p.orden_salida_paquete);
    const movimientos = Array.isArray(orden.movimientos) ? orden.movimientos : [];
    const status = Number(orden.status);

    /* Un mismo producto puede devolverse suelto y, en otro renglón, como componente de un paquete
       distinto — hay que agrupar también por orden_salida_paquete (null para lo suelto) para no
       mezclar sus ubicaciones recibidas. */
    const movimientosPorProducto = movimientos.reduce((mapa, mov) => {
        const idProducto = mov?.producto?._id;
        if (!idProducto) return mapa;

        const key = `${idProducto}_${mov?.orden_salida_paquete || 'standalone'}`;
        if (!mapa[key]) mapa[key] = [];
        mapa[key].push(mov);

        return mapa;
    }, {});

    return (
        <Fragment>
            <div className="row">
                <div className="col-md-10 offset-md-1 pb-4">
                    <div className="d-flex align-items-center gap-2 mb-1 mt-5">
                        <h3 className="mb-0">Devolución {orden?.folio || ''}</h3>
                        {renderBadgeStatus(orden?.status)}
                    </div>
                    <h6 className="text-muted mb-4">{orden?.proyecto?.nombre || 'Proyecto'}</h6>

                    <div className="row mb-4">
                        <div className="col-md-3">
                            <div className="text-muted small">Fecha</div>
                            <div>{formatDate(orden?.fecha)}</div>
                        </div>
                        <div className="col-md-3">
                            <div className="text-muted small">Dirección</div>
                            <div>{orden?.proyecto?.direccion || 'Sin definir'}</div>
                        </div>
                        <div className="col-md-6">
                            <div className="text-muted small">Observaciones</div>
                            <div>{orden?.observaciones || 'Sin definir'}</div>
                        </div>
                    </div>

                    <h5 className="mb-3">Productos</h5>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th>Producto</th>
                                    <th className="text-end">Cantidad</th>
                                    <th>Unidad</th>
                                    <th>Ubicación recibida</th>
                                </tr>
                            </thead>
                            <tbody>
                                {productos.length === 0 && (
                                    <tr>
                                        <td colSpan="4" className="text-center text-muted">
                                            Sin productos
                                        </td>
                                    </tr>
                                )}

                                {productos.map((p) => {
                                    const movimientosProducto = movimientosPorProducto[`${p.producto?._id}_${p.orden_salida_paquete || 'standalone'}`] || [];

                                    return (
                                        <tr key={p._id}>
                                            <td>
                                                {p.producto?.nombre || 'Sin definir'}
                                                {p.orden_salida_paquete ? (
                                                    <div><span className="badge bg-secondary" style={{ fontSize: 10 }}>Componente de paquete</span></div>
                                                ) : null}
                                            </td>
                                            <td className="text-end">{formatNumber(p.cantidad)}</td>
                                            <td>{p.unidad?.nombre || '—'}</td>
                                            <td>
                                                {movimientosProducto.length === 0 ? (
                                                    '—'
                                                ) : (
                                                    <ul className="list-unstyled mb-0">
                                                        {movimientosProducto.map((mov) => (
                                                            <li key={mov._id}>
                                                                {buildUbicacionLabel(mov)}
                                                                <span className="text-muted"> · {formatNumber(mov.cantidad)}</span>
                                                            </li>
                                                        ))}
                                                    </ul>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <h5 className="mb-3">Paquetes</h5>

                    <div className="text-muted mb-2" style={{ fontSize: 13 }}>
                        Paquetes devueltos completos. El inventario se acreditó producto por producto entre los componentes del paquete —
                        despliega el paquete para ver la ubicación recibida de cada componente.
                    </div>

                    <div className="table-responsive mb-4">
                        <table className="table table-striped table-hover">
                            <thead>
                                <tr>
                                    <th style={{ width: 30 }} />
                                    <th>Paquete</th>
                                    <th className="text-end">Cantidad</th>
                                </tr>
                            </thead>
                            <tbody>
                                {paquetes.length === 0 && (
                                    <tr>
                                        <td colSpan="3" className="text-center text-muted">
                                            Sin paquetes
                                        </td>
                                    </tr>
                                )}

                                {paquetes.map((p) => {
                                    const abierto = !!paquetesExpandidos[p._id];
                                    const cargando = !!cargandoComponentes[p._id];
                                    const componentes = componentesPorPaquete[p._id] || [];
                                    const cantidadPaquetes = Number(p.cantidad || 0);

                                    return (
                                        <Fragment key={p._id}>
                                            <tr>
                                                <td className="text-center align-middle">
                                                    <button
                                                        type="button"
                                                        className="btn btn-sm btn-link p-0"
                                                        onClick={() => togglePaqueteExpandido(p._id, p.paquete?._id)}
                                                        title={abierto ? 'Ocultar productos del paquete' : 'Ver productos del paquete'}
                                                    >
                                                        <i className={`fas fa-chevron-${abierto ? 'down' : 'right'}`} />
                                                    </button>
                                                </td>
                                                <td className="align-middle">
                                                    {p.paquete?.nombre || 'Sin definir'}
                                                    {p.paquete?.codigo ? (
                                                        <div className="text-muted" style={{ fontSize: 12 }}>{p.paquete.codigo}</div>
                                                    ) : null}
                                                </td>
                                                <td className="text-end align-middle">{formatNumber(cantidadPaquetes)}</td>
                                            </tr>
                                            {abierto && (
                                                <tr>
                                                    <td />
                                                    <td colSpan={2} className="p-0">
                                                        <div className="table-responsive m-2">
                                                            <table className="table table-sm mb-0" style={{ fontSize: 13 }}>
                                                                <thead>
                                                                    <tr>
                                                                        <th>Producto</th>
                                                                        <th className="text-center" style={{ width: 130 }}>Cantidad</th>
                                                                        <th style={{ width: 150 }}>Unidad</th>
                                                                        <th>Ubicación recibida</th>
                                                                    </tr>
                                                                </thead>
                                                                <tbody>
                                                                    {cargando ? (
                                                                        <tr>
                                                                            <td colSpan={4} className="text-muted text-center">Cargando productos...</td>
                                                                        </tr>
                                                                    ) : componentes.length === 0 ? (
                                                                        <tr>
                                                                            <td colSpan={4} className="text-muted text-center">Sin productos</td>
                                                                        </tr>
                                                                    ) : componentes.map((c) => {
                                                                        const conversion = Number(c?.conversion || 1) || 1;
                                                                        const necesaria = (Number(c?.cantidad || 0) * cantidadPaquetes) / conversion;
                                                                        const movimientosComponente = movimientosPorProducto[`${c?.producto?._id}_${p.orden_salida_paquete}`] || [];

                                                                        return (
                                                                            <tr key={c?.producto?._id}>
                                                                                <td>{c?.producto?.nombre || 'Sin definir'}</td>
                                                                                <td className="text-center">{formatNumber(necesaria)}</td>
                                                                                <td>{c?.unidad?.nombre || '-'}</td>
                                                                                <td>
                                                                                    {movimientosComponente.length === 0 ? (
                                                                                        '—'
                                                                                    ) : (
                                                                                        <ul className="list-unstyled mb-0">
                                                                                            {movimientosComponente.map((mov) => (
                                                                                                <li key={mov._id}>
                                                                                                    {buildUbicacionLabel(mov)}
                                                                                                    <span className="text-muted"> · {formatNumber(mov.cantidad)}</span>
                                                                                                </li>
                                                                                            ))}
                                                                                        </ul>
                                                                                    )}
                                                                                </td>
                                                                            </tr>
                                                                        );
                                                                    })}
                                                                </tbody>
                                                            </table>
                                                        </div>
                                                    </td>
                                                </tr>
                                            )}
                                        </Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>

                    <div className="d-flex justify-content-end align-items-center">
                        <div className="d-flex gap-2">
                            <button
                                type="button"
                                className="btn btn-secondary"
                                onClick={() => navigate(`/proyectos/detalle/${orden?.proyecto?._id || ''}`)}
                            >
                                Volver
                            </button>

                            {status === 1 && (
                                <button
                                    type="button"
                                    className="btn btn-outline-primary"
                                    onClick={() => navigate(`/proyectos/devoluciones/editar/${id}`)}
                                >
                                    <i className="fa fa-edit" /> Editar
                                </button>
                            )}

                            {status === 1 && (
                                <button
                                    type="button"
                                    className="btn btn-success"
                                    onClick={() => setModalCompletar(true)}
                                >
                                    <i className="fas fa-check" /> Completar devolución
                                </button>
                            )}

                            {status !== 2 && (
                                <button
                                    type="button"
                                    className="btn btn-danger"
                                    onClick={() => setModalEliminar(true)}
                                >
                                    Eliminar
                                </button>
                            )}
                        </div>
                    </div>
                </div>
            </div>

            <CompletarOrdenDevolucionModal
                show={modalCompletar}
                orden={orden}
                onHide={() => setModalCompletar(false)}
                onSuccess={recargar}
            />

            <EliminarOrdenDevolucionModal
                show={modalEliminar}
                idOrden={id}
                idProyecto={orden?.proyecto?._id || null}
                onHide={() => setModalEliminar(false)}
                onSuccess={() => navigate(`/proyectos/detalle/${orden?.proyecto?._id || ''}`)}
            />
        </Fragment>
    );
};

export default DetalleOrdenDevolucion;
